import { create } from "zustand";
import type { ReportContent } from "@/features/ws4-report/report-content";
import type { AnalysisContent } from "@/features/ws4-analysis/analysis-content";
import type { WorkshopDataTables } from "@/features/ws4-data-tables/data-tables-content";

/**
 * Synchronous-readable cache of BFF-generated Report/Analysis/Data-Tables
 * content (ADO #685), fed by `useGeneratedReport`/`useGeneratedAnalysis`/
 * `useGeneratedDataTables` as a write-through side effect of their fetch.
 *
 * Those hooks are plain `useState`/`useEffect` (async, component-local) —
 * fine for the modals, but `resolveOutputContent` in
 * `workshop-content-context.ts` (the chat's context-injection path) is
 * synchronous and has no React lifecycle to await. This store bridges that
 * gap the same way `useTranscripts`/`useCaptures` already do for their own
 * data: whichever component mounts first and fetches the generated content
 * populates the cache here, and `resolveOutputContent` reads it via
 * `getState()` without fetching anything itself.
 *
 * A workshop/kind absent from these maps means "not fetched yet or nothing
 * generated" — `resolveOutputContent` treats that as "no generated content"
 * and falls back to the static WS4 fixture (with the #683 disclaimer if a
 * live transcript exists), never the other way around. This is a read-only
 * cache — it never fetches on its own and is never treated as an error state.
 */
export type GeneratedWorkshopContentState = {
  report: Record<string, ReportContent>;
  analysis: Record<string, AnalysisContent>;
  dataTables: Record<string, WorkshopDataTables>;
  setReport: (workshopId: string, content: ReportContent | null) => void;
  setAnalysis: (workshopId: string, content: AnalysisContent | null) => void;
  setDataTables: (workshopId: string, content: WorkshopDataTables | null) => void;
};

export const useGeneratedWorkshopContent = create<GeneratedWorkshopContentState>((set) => ({
  report: {},
  analysis: {},
  dataTables: {},
  setReport: (workshopId, content) =>
    set((s) => ({ report: withEntry(s.report, workshopId, content) })),
  setAnalysis: (workshopId, content) =>
    set((s) => ({ analysis: withEntry(s.analysis, workshopId, content) })),
  setDataTables: (workshopId, content) =>
    set((s) => ({ dataTables: withEntry(s.dataTables, workshopId, content) })),
}));

/** `null` (fetched, nothing generated yet) clears any stale cached entry
 *  rather than caching a `null` — callers only ever store real content. */
function withEntry<T>(
  map: Record<string, T>,
  workshopId: string,
  content: T | null,
): Record<string, T> {
  if (content === null) {
    if (!(workshopId in map)) return map;
    const { [workshopId]: _removed, ...rest } = map;
    return rest;
  }
  return { ...map, [workshopId]: content };
}
